import axios from 'axios'
import { escapePrintTitle } from './printTitle'
import { CapacitorBarcodeScanner } from '@capacitor/barcode-scanner';
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Browser } from '@capacitor/browser'
import { registerPlugin } from '@capacitor/core'
import { Dialog } from '@capacitor/dialog'
import { Share } from '@capacitor/share'
import { formatDecimal } from '../decimal'

const TOKEN_KEY = 'jwt';
const PROFILE_REFRESH_TTL_MS = 60000;
const ACCOUNT_REFRESH_TTL_MS = 60000;
const TOKEN_REFRESH_LEEWAY_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_RETRY_MS = 60 * 1000;
const CROSS_TAB_REFRESH_WAIT_MS = 2000;
export const SESSION_EXPIRED_EVENT = 'kartustok:session-expired';
export const SESSION_RENEWED_EVENT = 'kartustok:session-renewed';
export const SESSION_RETRY_ACTION_EVENT = 'kartustok:session-retry-action';

let profileRefreshInFlight = null;
let lastProfileRefreshAt = 0;
let accountRefreshInFlight = null;
let accountRefreshInFlightAccessToken = null;
let accountRefreshInFlightSubdomain = null;
let lastAccountRefreshAt = 0;
let lastAccountRefreshAccessToken = null;
let lastAccountRefreshSubdomain = null;
let tokenRefreshInFlight = null;
let tokenRefreshTimer = null;
let sessionRefreshInitialized = false;
let sessionInterceptorsInitialized = false;
let sessionExpired = false;
let sessionExpiredToken = null;
let sessionExpiredIdentity = null;

const AndroidDownload = registerPlugin('AndroidDownload')


function validateNumber(num) {
  return !!(num || num === 0) && typeof num === 'number' && !Number.isNaN(Number(num));
};

function validateString() {
  var str = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return !!(typeof str === 'string' && str);
};

function isRequired() {
  var param = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'param';
  throw new Error("".concat(param, " is required"));
};

function invalidType() {
  var param = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'param';
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'type';
  throw new Error("".concat(param, " must be a valid ").concat(type));
};

function minutesToMiliseconds() {
  var minutes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 5;
  var parseMinutes = !validateNumber(minutes) || minutes < 1 ? 5 : minutes;
  return parseMinutes * 60000;
};

function getItem() {
  var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : isRequired('name localstorage');
  if (!validateString(name)) invalidType('name', 'string');
  var itemStr = window.localStorage.getItem(name);

  if (!itemStr) {
    return null;
  }

  var item = JSON.parse(itemStr);

  if (item.expiry < new Date().getTime()) {
    window.localStorage.removeItem(name);
    return null;
  }

  return item.value;
};

function setItem() {
  var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : isRequired('name for window.localStorage');
  var value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : isRequired('value for window.localStorage');
  var expiry = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 5;
  if (!validateString(name)) invalidType('name', 'string');
  var validExpiry = !validateNumber(expiry) ? 60000 : minutesToMiliseconds(expiry);
  var item = {
    value: value,
    expiry: new Date().getTime() + validExpiry
  };
  window.localStorage.setItem(name, JSON.stringify(item));
};

//get subdomain
export const getSubdomain = () => {
  if (window.platform.isMobile) {
    if (getData('subdomain')) {
      return getData('subdomain');
    }

    return 'member';
  } else {
    const arrDomain = window.location.hostname.split('.');

    if (process.env.REACT_APP_SUBDOMAIN !== undefined && process.env.REACT_APP_SUBDOMAIN !== '') {
      return process.env.REACT_APP_SUBDOMAIN;
    }

    return arrDomain[0];
  }
}

export const pushIfChanged = (history, targetUrl) => {
  if (!history || !targetUrl) {
    return;
  }

  const currentUrl = history.location
    ? `${history.location.pathname || ''}${history.location.search || ''}${history.location.hash || ''}`
    : (window.location.hash || '').replace(/^#/, '');

  if (currentUrl !== targetUrl) {
    history.push(targetUrl);
  }
}

export const resolveDownloadUrl = (rawUrl) => {
  if (!rawUrl) {
    return rawUrl;
  }

  try {
    const downloadUrl = new URL(rawUrl, window.location.origin);
    const localDevHosts = ['kartustokbe.local', 'kartustokfe.local'];
    const isLocalDevHost = localDevHosts.some((suffix) => downloadUrl.hostname.endsWith(suffix));

    if (window.location.port && !downloadUrl.port && isLocalDevHost) {
      downloadUrl.port = window.location.port;
    }

    return downloadUrl.toString();
  } catch (error) {
    return rawUrl;
  }
}

export const buildAppUrl = (path, subdomain = getSubdomain()) => {
  const targetPath = path || '/';

  try {
    const currentUrl = new URL(window.location.origin);
    const hostnameParts = currentUrl.hostname.split('.');

    if (hostnameParts.length > 0 && subdomain) {
      hostnameParts[0] = subdomain;
    }

    currentUrl.hostname = hostnameParts.join('.');
    currentUrl.hash = targetPath.startsWith('#') ? targetPath : '#'+targetPath;

    return currentUrl.toString();
  } catch (error) {
    return '//' + subdomain + '.' + process.env.REACT_APP_DOMAIN + '/#' + targetPath.replace(/^#/, '');
  }
}

export const buildMobilePrintHtml = ({
  rootSelector = '#printable-area',
  title = document.title,
  removeSelectors = ['#btn-actions', '.report-print-actions', '.report-summary-print-actions'],
  extraStyles = '',
  autoPrint = false
} = {}) => {
  const source = document.querySelector(rootSelector);

  if (!source) {
    return null;
  }

  const clone = source.cloneNode(true);

  removeSelectors.forEach((selector) => {
    clone.querySelectorAll(selector).forEach((node) => node.remove());
  });

  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map((node) => {
    if (node.tagName.toLowerCase() === 'style') {
      return node.outerHTML;
    }

    const href = node.getAttribute('href');

    if (!href) {
      return '';
    }

    try {
      const absoluteHref = new URL(href, window.location.href).toString();
      return `<link rel="stylesheet" href="${absoluteHref}">`;
    } catch (error) {
      return '';
    }
  }).join('');

  const backgroundColor = window.getComputedStyle(document.body).backgroundColor || '#ffffff';
  const bodyClassName = document.body.className || '';
  const isTemplateRoot = rootSelector === '#print-template-root';
  const defaultPageStyles = isTemplateRoot ? '' : '@page { size: auto; margin: 20mm; }';
  const templatePageStyles = isTemplateRoot ? `
      html, body {
        width: auto !important;
        height: auto !important;
        min-width: 0 !important;
        min-height: 0 !important;
        overflow: visible !important;
      }

      body {
        background: #ffffff !important;
      }

      #print-template-root.pt-root-print,
      #print-template-root.pt-root-print * {
        box-sizing: border-box;
      }

      #print-template-root.pt-root-print {
        display: block !important;
        width: var(--pt-page-width) !important;
        max-width: var(--pt-page-width) !important;
        min-width: var(--pt-page-width) !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        background: #ffffff !important;
        transform: none !important;
      }

      #print-template-root.pt-root-print .pt-preview-shell {
        width: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        background: none !important;
        box-shadow: none !important;
      }

      #print-template-root.pt-root-print .pt-page {
        display: block !important;
        width: var(--pt-page-width) !important;
        max-width: var(--pt-page-width) !important;
        min-width: var(--pt-page-width) !important;
        min-height: var(--pt-page-height) !important;
        margin: 0 !important;
        box-shadow: none !important;
        transform: none !important;
        page-break-after: always;
        break-after: page;
      }

      #print-template-root.pt-root-print .pt-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }

      @media print {
        html, body {
          width: auto !important;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
        }

        body {
          background: #ffffff !important;
        }

        #print-template-root.pt-root-print {
          width: var(--pt-page-width) !important;
          max-width: var(--pt-page-width) !important;
          min-width: var(--pt-page-width) !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        #print-template-root.pt-root-print .pt-page {
          width: var(--pt-page-width) !important;
          max-width: var(--pt-page-width) !important;
          min-width: var(--pt-page-width) !important;
          min-height: var(--pt-page-height) !important;
          margin: 0 !important;
          box-shadow: none !important;
        }
      }
  ` : '';
  const autoPrintScript = autoPrint ? `
    <script>
      (function () {
        var hasScheduledClose = false;
        var hasTriggeredPrint = false;
        var fallbackCloseTimer = null;

        function scheduleClose() {
          if (hasScheduledClose) {
            return;
          }

          hasScheduledClose = true;

          setTimeout(function () {
            try {
              window.close();
            } catch (error) {}
          }, 150);
        }

        function handlePrintFinished() {
          if (!hasTriggeredPrint) {
            return;
          }

          scheduleClose();
        }

        function triggerPrint() {
          hasTriggeredPrint = true;
          window.focus();
          window.addEventListener('afterprint', handlePrintFinished, { once: true });
          window.addEventListener('focus', handlePrintFinished, { once: true });
          window.onafterprint = handlePrintFinished;
          window.print();
          fallbackCloseTimer = setTimeout(handlePrintFinished, 15000);
        }

        function waitForImages() {
          var images = Array.prototype.slice.call(document.images || []);

          if (!images.length) {
            setTimeout(triggerPrint, 300);
            return;
          }

          var pending = images.length;
          var done = false;

          function finish() {
            if (done) {
              return;
            }

            done = true;
            setTimeout(triggerPrint, 300);
          }

          function markReady() {
            pending -= 1;

            if (pending <= 0) {
              finish();
            }
          }

          images.forEach(function (image) {
            if (image.complete) {
              markReady();
              return;
            }

            image.addEventListener('load', markReady, { once: true });
            image.addEventListener('error', markReady, { once: true });
          });

          setTimeout(finish, 1500);
        }

        window.addEventListener('load', waitForImages, { once: true });
        window.addEventListener('beforeunload', function () {
          if (fallbackCloseTimer) {
            clearTimeout(fallbackCloseTimer);
          }
        }, { once: true });
      })();
    </script>
  ` : '';

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
    <base href="${window.location.origin}${window.location.pathname}">
    <title>${escapePrintTitle(title)}</title>
    ${styles}
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: ${backgroundColor};
      }
      ${defaultPageStyles}
      ${templatePageStyles}
      ${extraStyles}
    </style>
  </head>
  <body class="${bodyClassName}">
    ${clone.outerHTML}
    ${autoPrintScript}
  </body>
</html>`;
}

export const printSnapshotInNewWindow = ({
  rootSelector = '#printable-area',
  title = document.title,
  removeSelectors = ['#btn-actions', '.report-print-actions', '.report-summary-print-actions'],
  extraStyles = ''
} = {}) => {
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    return false;
  }

  const printHtml = buildMobilePrintHtml({
    rootSelector,
    title,
    removeSelectors,
    extraStyles,
    autoPrint: true
  });

  if (!printHtml) {
    printWindow.close();
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();

  return true;
}

//store data to local storage
export const storeData = (key, value, expiry = 5) => {
  setItem(key, value, expiry);
}

//remove local storage data
export const removeData = (key) => {
  window.localStorage.removeItem(key);
}

//return local storage data
export const getData = (key) => {
  if (getItem(key)) {
    return getItem(key);
  }

  return false;
}

const storedLoginEnvelope = () => {
  try {
    const item = JSON.parse(window.localStorage.getItem(TOKEN_KEY));
    return item && item.expiry > Date.now() && typeof item.value === 'string' ? item : null;
  } catch (error) {
    return null;
  }
}

const sessionIdentity = (data) => {
  const profile = data && data.profile;
  const id = profile && (profile.id || profile.email);
  return id ? `${id}:${profile.role?.company_id || getSubdomain()}` : null;
}

const sameSessionIdentity = (first, second) => {
  const identity = sessionIdentity(first);
  return Boolean(identity && identity === sessionIdentity(second));
}

const clearSessionExpired = () => {
  if (sessionExpired) {
    sessionExpired = false;
    sessionExpiredToken = null;
    sessionExpiredIdentity = null;
    window.dispatchEvent(new Event(SESSION_RENEWED_EVENT));
  }
}

const notifySessionExpired = () => {
  if (!sessionExpired && getLoginData()) {
    sessionExpired = true;
    sessionExpiredToken = getLoginData().access_token;
    sessionExpiredIdentity = sessionIdentity(getLoginData());
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
}

export const isSessionExpired = () => sessionExpired;

const matchesRefreshedSession = (previous, refreshed) => Boolean(
  refreshed?.access_token && (!sessionIdentity(previous) || sameSessionIdentity(previous, refreshed))
);

// Profile updates must keep the original browser-session duration.
export const storeLoginData = (data) => {
  const envelope = storedLoginEnvelope();
  if (!envelope) {
    return false;
  }

  const current = getLoginData();
  if (!current) {
    return false;
  }

  const next = typeof data === 'string' ? JSON.parse(data) : data;
  const duration = Number(next.session_expiry_minutes || current.session_expiry_minutes);
  const expiry = Number.isFinite(duration) && duration > 0
    ? Date.now() + duration * 60000
    : envelope.expiry;
  window.localStorage.setItem(TOKEN_KEY, JSON.stringify({
    value: JSON.stringify(next),
    expiry,
  }));
  if (sessionExpired && sessionExpiredIdentity && next.access_token !== sessionExpiredToken && sessionIdentity(next) === sessionExpiredIdentity) {
    clearSessionExpired();
  }
  scheduleTokenRefresh();
  return true;
}

//store login user data
export const login = (theData, expiry = 1440) => {
  let storedData = theData;

  try {
    const parsed = typeof theData === 'string' ? JSON.parse(theData) : theData;
    if (parsed && parsed.access_token) {
      const expiresIn = Number(parsed.expires_in);
      const accessTokenExpiresAt = Number(parsed.access_token_expires_at)
        || (Number.isFinite(expiresIn) && expiresIn > 0 ? Date.now() + (expiresIn * 1000) : 0);

      storedData = JSON.stringify({
        ...parsed,
        access_token_expires_at: accessTokenExpiresAt,
        session_expiry_minutes: expiry,
      });
    }
  } catch (error) {
    storedData = theData;
  }

  setItem(TOKEN_KEY, storedData, expiry);
  clearSessionExpired();
  scheduleTokenRefresh();
}

//refresh token
const waitForRotatedSession = (previous, subdomain) => new Promise(resolve => {
  const updatedSession = () => {
    const latest = getLoginData();
    return getSubdomain() === subdomain && latest?.access_token !== previous.access_token
      && sameSessionIdentity(previous, latest) ? latest : null;
  };

  const current = updatedSession();
  if (current) {
    resolve(current);
    return;
  }

  const onStorage = (event) => {
    if (event.key === TOKEN_KEY) {
      const latest = updatedSession();
      if (latest) {
        clearTimeout(timer);
        window.removeEventListener('storage', onStorage);
        resolve(latest);
      }
    }
  };
  const timer = setTimeout(() => {
    window.removeEventListener('storage', onStorage);
    resolve(updatedSession());
  }, CROSS_TAB_REFRESH_WAIT_MS);
  window.addEventListener('storage', onStorage);
});

export const refreshToken = () => {
  if (tokenRefreshInFlight) {
    return tokenRefreshInFlight;
  }

  const loginData = getLoginData();
  if (!loginData || !loginData.refresh_token) {
    return Promise.resolve(null);
  }

  const refreshAccessToken = loginData.access_token;
  const refreshSubdomain = getSubdomain();
  tokenRefreshInFlight = axios.post(
    process.env.REACT_APP_API_URL.replace('member', refreshSubdomain) + '/auth/token',
    {
      grant_type: 'refresh_token',
      refresh_token: loginData.refresh_token
    },
    {
      auth: {
        username: process.env.REACT_APP_API_CLIENT_ID,
        password: process.env.REACT_APP_API_CLIENT_SECRET
      }
    }
  ).then(response => {
    if (!response.data || !response.data.access_token) {
      const error = new Error('Token refresh did not return an access token.');
      error.authenticationRejected = Boolean(response.data && response.data.error);
      throw error;
    }

    const latestLoginData = getLoginData();
    if (!latestLoginData || latestLoginData.access_token !== refreshAccessToken || getSubdomain() !== refreshSubdomain) {
      return sameSessionIdentity(loginData, latestLoginData) ? latestLoginData : null;
    }

    const refreshedExpiresIn = Number(response.data.expires_in);
    const refreshedData = {
      ...loginData,
      ...response.data,
      profile: loginData.profile,
      access_token_expires_at: Number.isFinite(refreshedExpiresIn) && refreshedExpiresIn > 0
        ? Date.now() + (refreshedExpiresIn * 1000)
        : 0,
    };
    storeLoginData(refreshedData);
    clearSessionExpired();
    return getLoginData();
  }).catch(async error => {
    const status = error && error.response ? error.response.status : null;
    if (status === 400 || status === 401 || error.authenticationRejected) {
      const rotated = await waitForRotatedSession(loginData, refreshSubdomain);
      if (rotated) {
        clearSessionExpired();
        return rotated;
      }
      const latest = getLoginData();
      if (latest && latest.access_token === refreshAccessToken && getSubdomain() === refreshSubdomain) {
        notifySessionExpired();
      }
    } else {
      scheduleTokenRefresh(TOKEN_REFRESH_RETRY_MS);
    }
    throw error;
  }).finally(() => {
    tokenRefreshInFlight = null;
  });

  return tokenRefreshInFlight;
}

//remove login user data
export const logout = (revokeRemote = false) => {
  const loginData = getLoginData();
  const subdomain = getSubdomain();
  clearTokenRefreshTimer();
  window.localStorage.removeItem(TOKEN_KEY);

  if (revokeRemote && loginData) {
    const revokeUrl = process.env.REACT_APP_API_URL.replace('member', subdomain) + '/auth/revoke';
    const config = {
      auth: {
        username: process.env.REACT_APP_API_CLIENT_ID,
        password: process.env.REACT_APP_API_CLIENT_SECRET
      }
    };

    [loginData.access_token, loginData.refresh_token].filter(Boolean).forEach(token => {
      const request = axios.post(revokeUrl, { token }, config);
      if (request && typeof request.catch === 'function') {
        request.catch(() => {});
      }
    });
  }
}

const clearTokenRefreshTimer = () => {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}

const scheduleTokenRefresh = (overrideDelay = null) => {
  clearTokenRefreshTimer();
  const loginData = getLoginData();
  if (!loginData || !loginData.refresh_token || sessionExpired) {
    return;
  }

  const expiresAt = Number(loginData.access_token_expires_at);
  const delay = overrideDelay === null
    ? (!Number.isFinite(expiresAt) || expiresAt <= 0 ? 0 : Math.max(0, expiresAt - Date.now() - TOKEN_REFRESH_LEEWAY_MS))
    : Math.max(0, Number(overrideDelay) || 0);
  tokenRefreshTimer = setTimeout(() => {
    refreshToken().catch(() => {});
  }, Math.min(delay, 2147483647));
}

const bearerToken = (config) => {
  const headers = config && config.headers;
  const authorization = headers?.get ? headers.get('Authorization') : (headers?.Authorization || headers?.authorization);
  return typeof authorization === 'string' && authorization.startsWith('Bearer ')
    ? authorization.slice(7) : null;
}

const setBearerToken = (config, token) => {
  if (config.headers?.set) {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
}

const isCurrentApiRequest = (config) => {
  const base = process.env.REACT_APP_API_URL;
  if (!base) {
    return false;
  }
  const currentBase = base.replace('member', getSubdomain());
  return config?.url === currentBase || config?.url?.startsWith(`${currentBase}/`);
}

const sessionExpiredError = () => {
  const message = getData('lang') === 'ID'
    ? 'Sesi berakhir. Silakan masuk kembali.'
    : 'Session expired. Please sign in again.';
  const error = new Error(message);
  error.sessionExpired = true;
  // Existing form handlers expect Axios errors to have response.data.message.
  error.response = { status: 401, data: { message } };
  return error;
}

const installSessionInterceptors = () => {
  if (sessionInterceptorsInitialized || !axios.interceptors) {
    return;
  }
  sessionInterceptorsInitialized = true;

  axios.interceptors.request.use(async config => {
    const token = bearerToken(config);
    if (!token || !isCurrentApiRequest(config)) {
      return config;
    }
    const current = getLoginData();
    if (!current || token !== current.access_token) {
      return config;
    }
    config._sessionIdentity = sessionIdentity(current);
    if (sessionExpired) {
      throw sessionExpiredError();
    }
    const expiresAt = Number(current.access_token_expires_at);
    if (current.refresh_token && (!expiresAt || expiresAt <= Date.now() + TOKEN_REFRESH_LEEWAY_MS)) {
      let refreshed;
      try {
        refreshed = await refreshToken();
      } catch (error) {
        throw sessionExpired ? sessionExpiredError() : error;
      }
      if (!matchesRefreshedSession(current, refreshed)) {
        throw sessionExpiredError();
      }
      setBearerToken(config, refreshed.access_token);
    }
    return config;
  });

  axios.interceptors.response.use(response => response, async error => {
    const config = error?.config;
    const token = bearerToken(config);
    if (error?.response?.status !== 401 || !token || !isCurrentApiRequest(config)) {
      return Promise.reject(error);
    }
    const current = getLoginData();
    if (!current || (config._sessionIdentity && config._sessionIdentity !== sessionIdentity(current))) {
      return Promise.reject(error);
    }
    if (config._sessionRetried) {
      notifySessionExpired();
      return Promise.reject(error);
    }
    if (current.access_token !== token && !config._sessionIdentity) {
      return Promise.reject(error);
    }

    let renewed = current;
    if (current.access_token === token) {
      if (!current.refresh_token) {
        notifySessionExpired();
        return Promise.reject(error);
      }
      try {
        renewed = await refreshToken();
      } catch (refreshError) {
        return Promise.reject(sessionExpired ? sessionExpiredError() : refreshError);
      }
    }

    if (!matchesRefreshedSession(current, renewed)) {
      return Promise.reject(error);
    }
    if ((config.method || 'get').toLowerCase() !== 'get') {
      window.dispatchEvent(new Event(SESSION_RETRY_ACTION_EVENT));
      const message = getData('lang') === 'ID'
        ? 'Sesi diperbarui. Periksa lalu coba lagi; tindakan tidak dikirim ulang.'
        : 'Session renewed. Review and retry; the action was not submitted again.';
      error.response = { ...error.response, data: { ...error.response.data, message } };
      return Promise.reject(error);
    }

    config._sessionRetried = true;
    setBearerToken(config, renewed.access_token);
    return axios(config);
  });
}

export const initializeSessionRefresh = () => {
  installSessionInterceptors();
  scheduleTokenRefresh();
  if (sessionRefreshInitialized || typeof window === 'undefined') {
    return;
  }

  const refreshIfNeeded = () => {
    if (sessionExpired) {
      return;
    }
    const loginData = getLoginData();
    const expiresAt = loginData ? Number(loginData.access_token_expires_at) : 0;
    if (loginData && loginData.refresh_token && (!expiresAt || expiresAt <= Date.now() + TOKEN_REFRESH_LEEWAY_MS)) {
      refreshToken().catch(() => {});
    }
  };

  window.addEventListener('focus', refreshIfNeeded);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshIfNeeded();
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === TOKEN_KEY) {
      const latest = getLoginData();
      if (sessionExpired && sessionExpiredIdentity && latest?.access_token !== sessionExpiredToken && sessionIdentity(latest) === sessionExpiredIdentity) {
        clearSessionExpired();
      }
      scheduleTokenRefresh();
    }
  });
  sessionRefreshInitialized = true;
}

//check whether user is logged or not
export const isLogin = () => {
  if (getItem(TOKEN_KEY)) {
    return true;
  }

  return false;
}

//return login user data
export const getLoginData = () => {
  if (getItem(TOKEN_KEY)) {
    return JSON.parse(getItem(TOKEN_KEY));
  }

  return false;
}

//update account
export const updateAccount = () => {
  const loginData = getLoginData();

  if (!loginData) {
    return null;
  }

  const refreshAccessToken = loginData.access_token;
  const refreshSubdomain = getSubdomain();
  const now = Date.now();

  if (
    accountRefreshInFlight &&
    accountRefreshInFlightAccessToken === refreshAccessToken &&
    accountRefreshInFlightSubdomain === refreshSubdomain
  ) {
    return accountRefreshInFlight;
  }

  if (
    lastAccountRefreshAt > 0 &&
    lastAccountRefreshAccessToken === refreshAccessToken &&
    lastAccountRefreshSubdomain === refreshSubdomain &&
    now - lastAccountRefreshAt < ACCOUNT_REFRESH_TTL_MS
  ) {
    return null;
  }

  const request = axios.get(process.env.REACT_APP_API_URL.replace('member', refreshSubdomain) + '/account/data', {
    headers: {
      Authorization: 'Bearer ' + refreshAccessToken
    }
  }).then(response => {
    if (response.data.status === 'success') {
      const latestLoginData = getLoginData();
      if (!latestLoginData || latestLoginData.access_token !== refreshAccessToken || getSubdomain() !== refreshSubdomain) {
        return;
      }

      storeData('account', JSON.stringify(response.data.data), (60 * 24 * 90));
      lastAccountRefreshAt = Date.now();
      lastAccountRefreshAccessToken = refreshAccessToken;
      lastAccountRefreshSubdomain = refreshSubdomain;
      return response.data.data;
    }

    return null;
  }).catch(() => {
    // Best-effort background refresh; ignore transient failures.
    return null;
  }).finally(() => {
    if (accountRefreshInFlight === request) {
      accountRefreshInFlight = null;
      accountRefreshInFlightAccessToken = null;
      accountRefreshInFlightSubdomain = null;
    }
  });

  accountRefreshInFlight = request;
  accountRefreshInFlightAccessToken = refreshAccessToken;
  accountRefreshInFlightSubdomain = refreshSubdomain;

  return accountRefreshInFlight;
}

//check permissions
export const getAccountData = () => {
  const accountData = getData('account');

  if (accountData) {
    return JSON.parse(accountData);
  }

  return false;
}

export const isWarehouseGroupingEnabled = () => {
  const accountData = getAccountData();

  return accountData?.details?.enable_warehouse_grouping === 1 || accountData?.details?.enable_warehouse_grouping === '1';
}

export const getPriceSettings = () => {
  const accountData = getAccountData();

  const enablePrice = accountData?.details?.enable_price === 1 || accountData?.details?.enable_price === '1';
  const rawPrecision = accountData?.details?.price_decimal;
  const parsedPrecision = Number.parseInt(rawPrecision, 10);
  const priceDecimal = Number.isFinite(parsedPrecision) ? Math.min(Math.max(parsedPrecision, 0), 4) : 0;

  return {
    enabled: enablePrice,
    precision: priceDecimal,
  };
}

export const isPriceEnabled = () => {
  return getPriceSettings().enabled;
}

export const canViewPriceFeature = () => {
  return isPriceEnabled() && checkPermissions(['viewPrice']);
}

export const canEditPriceFeature = () => {
  return isPriceEnabled() && checkPermissions(['editPrice']);
}

export const formatPrice = (value, precision = null) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  const fallbackPrecision = getPriceSettings().precision;
  const targetPrecision = Number.isFinite(Number.parseInt(precision, 10))
    ? Math.min(Math.max(Number.parseInt(precision, 10), 0), 4)
    : fallbackPrecision;

  return formatDecimal(value, targetPrecision);
}
//update user profile
export const updateProfile = () => {
  const loginData = getLoginData();

  if (!loginData) {
    return null;
  }

  const now = Date.now();
  if (profileRefreshInFlight) {
    return profileRefreshInFlight;
  }

  if (lastProfileRefreshAt > 0 && now - lastProfileRefreshAt < PROFILE_REFRESH_TTL_MS) {
    return null;
  }

  const refreshAccessToken = loginData.access_token;
  const refreshSubdomain = getSubdomain();

  profileRefreshInFlight = axios.get(process.env.REACT_APP_API_URL.replace('member', refreshSubdomain) + '/user/profile', {
    headers: {
      Authorization: 'Bearer ' + refreshAccessToken
    }
  }).then(response => {
    if (response.data.status === 'success') {
      const latestLoginData = getLoginData();
      if (!latestLoginData || latestLoginData.access_token !== refreshAccessToken || getSubdomain() !== refreshSubdomain) {
        return;
      }

      latestLoginData['profile'] = response.data.data;

      storeLoginData(latestLoginData);
      lastProfileRefreshAt = Date.now();
      window.dispatchEvent(new CustomEvent('kartustok:profile-updated', { detail: response.data.data }));
    }
  }).catch(() => {
    // Best-effort background refresh; ignore transient failures.
  }).finally(() => {
    profileRefreshInFlight = null;
  });

  return profileRefreshInFlight;
}

//check permissions
export const checkPermissions = (perms) => {
  const loginData = getLoginData();

  if (loginData) {
    const permissions = loginData.profile?.role?.permissions;

    if (permissions) {
      if (permissions.filter(perm => perms.includes(perm.name)).length > 0) {
        return true;
      }
    }
  }

  return false;
}

// launch scanner
export var isScannerRunning = false

export const launchScanner = async () => {
  const result = await CapacitorBarcodeScanner.scanBarcode({});
  // const permission = await CapacitorBarcodeScanner.checkPermission({ force: true });
  // if (!permission.granted) return

  // document.getElementsByTagName('body')[0].style.display = 'none'

  // isScannerRunning = true
  // const result = await BarcodeScanner.startScan()
  // isScannerRunning = false

  // document.getElementsByTagName('body')[0].style.display = 'block'

  if (result) {
    return {
      contents: result.ScanResult
    }
  }

  return null
}

export const stopScanner = async () => {
  isScannerRunning = false
  // document.getElementsByTagName('body')[0].style.display = 'block'
  // BarcodeScanner.stopScan()
}

const sanitizeDownloadFileName = (fileName) => {
  const baseName = String(fileName || 'download').split(/[\\/]/).pop()
  return baseName.replace(/[<>:"|?*\u0000-\u001F]/g, '_').trim() || 'download'
}

const getMobileDownloadCopy = () => {
  if (getData('lang') === 'ID') {
    return {
      savedTitle: 'File berhasil disimpan',
      savedMessage: 'File telah disimpan di folder {folder}. Apakah Anda ingin membuka atau membagikannya?',
      savedOnlyMessage: 'File telah disimpan di folder {folder}.',
      shareButton: 'Buka/Bagikan',
      closeButton: 'Tutup',
      shareTitle: 'Buka atau bagikan file',
      shareUnavailableTitle: 'Tidak dapat membagikan file',
      shareUnavailableMessage: 'File tetap tersimpan dan dapat dibuka dari folder {folder}.',
      saveFailedTitle: 'Gagal menyimpan file',
      saveFailedMessage: 'File tidak dapat disimpan. Silakan coba kembali.',
    }
  }

  return {
    savedTitle: 'File saved',
    savedMessage: 'The file was saved in the {folder} folder. Would you like to open or share it?',
    savedOnlyMessage: 'The file was saved in the {folder} folder.',
    shareButton: 'Open/Share',
    closeButton: 'Close',
    shareTitle: 'Open or share file',
    shareUnavailableTitle: 'Unable to share file',
    shareUnavailableMessage: 'The file remains saved and can be opened from the {folder} folder.',
    saveFailedTitle: 'Unable to save file',
    saveFailedMessage: 'The file could not be saved. Please try again.',
  }
}

const showNativeDownloadAlert = async (title, message) => {
  try {
    await Dialog.alert({ title, message })
  } catch (error) {
    window.alert(`${title}\n${message}`)
  }
}

// Save first, then offer the native share sheet without making sharing mandatory.
export const downloadBase64File = async (base64Data, fileName) => {
  if (window.platform.isMobile) {
    const safeFileName = sanitizeDownloadFileName(fileName)
    const isIos = Boolean(window.platform.isIos)
    const persistentFolder = isIos ? 'Documents' : 'Download'
    const copy = getMobileDownloadCopy()

    try {
      if (isIos) {
        await Filesystem.writeFile({
          path: safeFileName,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true,
        })
      } else {
        const mimeTypeMatch = String(base64Data).match(/^data:([^;,]+)/i)
        await AndroidDownload.saveFile({
          fileName: safeFileName,
          data: base64Data,
          mimeType: mimeTypeMatch ? mimeTypeMatch[1] : 'application/octet-stream',
        })
      }

      let shareUri = null
      try {
        const cacheFile = await Filesystem.writeFile({
          path: `downloads/${Date.now()}-${safeFileName}`,
          data: base64Data,
          directory: Directory.Cache,
          recursive: true,
        })
        shareUri = cacheFile.uri
      } catch (error) {
        console.log('Unable to prepare downloaded file for sharing', error)
      }

      const message = copy.savedMessage.replace('{folder}', persistentFolder)
      let shouldShare = false
      if (shareUri) {
        try {
          const result = await Dialog.confirm({
            title: copy.savedTitle,
            message,
            okButtonTitle: copy.shareButton,
            cancelButtonTitle: copy.closeButton,
          })
          shouldShare = result.value
        } catch (error) {
          shouldShare = window.confirm(`${copy.savedTitle}\n${message}`)
        }
      } else {
        await showNativeDownloadAlert(
          copy.savedTitle,
          copy.savedOnlyMessage.replace('{folder}', persistentFolder)
        )
      }

      if (shouldShare) {
        try {
          const shareSupport = await Share.canShare()
          if (!shareSupport.value) {
            throw new Error('Native sharing is not available')
          }

          await Share.share({
            title: safeFileName,
            files: [shareUri],
            dialogTitle: copy.shareTitle,
          })
        } catch (error) {
          console.log('Unable to share downloaded file', error)
          await showNativeDownloadAlert(
            copy.shareUnavailableTitle,
            copy.shareUnavailableMessage.replace('{folder}', persistentFolder)
          )
        }
      }
    } catch (error) {
      console.log(error)
      await showNativeDownloadAlert(copy.saveFailedTitle, copy.saveFailedMessage)
    }
  } else {
    window.location = base64Data
  }
}

export const downloadBlobFile = (blob, fileName) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(downloadBase64File(reader.result, fileName))
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export const downloadAuthenticatedFile = async (rawUrl, fallbackFileName = 'download.xlsx') => {
  const response = await axios.get(resolveDownloadUrl(rawUrl), {
    headers: {
      Authorization: 'Bearer ' + getLoginData().access_token,
    },
    responseType: 'blob',
  })

  const disposition = response.headers && response.headers['content-disposition']
  const encodedMatch = disposition && disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition && disposition.match(/filename="?([^";]+)"?/i)
  let fileName = fallbackFileName
  if (encodedMatch && encodedMatch[1]) {
    fileName = decodeURIComponent(encodedMatch[1])
  } else if (plainMatch && plainMatch[1]) {
    fileName = plainMatch[1]
  }

  if (window.platform && window.platform.isMobile) {
    return downloadBlobFile(response.data, fileName)
  }

  const blobUrl = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = blobUrl
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000)
}

export const arrayBufferToBase64 = (buffer) => {
  var binary = '';
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export const openUrl = (url) => {
  Browser.open({ url: url })
}

export const getDownloadFolder = async () => {
  return await Filesystem.getUri({
    path: 'Download',
    directory: Directory.ExternalStorage,
  })
}
